import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { Router } from '@angular/router';
import { LoggerService } from '../logger/logger.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';

import { ITranscriptCorrection } from 'src/app/interfaces/itranscript-correction';
import { IAgentInteractionBlockRequest } from 'src/app/interfaces/call/conversational/iagent-interaction-block-request';
import { IPersonalizedLaunchPayload } from 'src/app/interfaces/personalized-practice/ipersonalized-launch';
import {
    IInitiatePathwayStepSimulationRequest,
    IInitiatePathwayStepSimulationResponse,
} from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { IProcessUserResponseResponse } from 'src/app/interfaces/call/conversational/iprocess-user-response-response';

@Injectable({
  providedIn: 'root',
})
export class CallService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private router: Router,
    private logger: LoggerService,
    private callStore: CallStoreService
  ) {}

  startCall(
    moduleAssignmentUuid: string,
    callUuid: string,
    practiceType: string,
    callType: string,
    acknowledgementSessionToken?: string | null
  ): Observable<any> {
    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    const payload: any = {
      userUuid: userUuid,
      moduleAssignmentUuid: moduleAssignmentUuid,
      callUuid: callUuid,
      practiceType: practiceType,
      callType: callType,
    };

    // Add acknowledgement token for certification calls
    if (acknowledgementSessionToken) {
      payload.acknowledgementSessionToken = acknowledgementSessionToken;
    }

    return this.http
      .post<any>(`${this.backendUrl}/startCall/initiateCallAndFetchCallInfo`, payload)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`postStartCall: ${outcome}`);
        }),
        catchError((error) => {
          // Log the error using the error handling service for Sentry/logging
          // But preserve the original error structure so it can be handled upstream
          const errorHandler =
            this.errorHandlingService.handleError<any>(`startCall request`);
          try {
            // Call the error handler - it may throw synchronously for some errors
            const result = errorHandler(error);
            // If it returns an Observable (like for 401), subscribe to it
            if (result instanceof Observable) {
              result.subscribe();
            }
          } catch (e) {
            // Error handling service throws for most errors (404, 500, 400, etc.)
            // This is expected - we've logged it, now re-throw the original error
          }
          // Re-throw the original error to preserve status code and structure
          return throwError(() => error);
        })
      );
  }

  initiatePathwayStepSimulation(
    moduleAssignmentUuid: string,
    callUuid: string,
    practiceType: string,
    callType: string,
    enrollmentStepUuid: string
  ): Observable<IInitiatePathwayStepSimulationResponse> {
    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<IInitiatePathwayStepSimulationResponse>();

    if (!moduleAssignmentUuid.trim())
      throw new Error('moduleAssignmentUuid is empty');
    if (!callUuid.trim()) throw new Error('callUuid is empty');
    if (!practiceType.trim()) throw new Error('practiceType is empty');
    if (!callType.trim()) throw new Error('callType is empty');
    if (!enrollmentStepUuid.trim())
      throw new Error('enrollmentStepUuid is empty');

    const body: IInitiatePathwayStepSimulationRequest = {
      userUuid,
      moduleAssignmentUuid,
      callUuid,
      practiceType,
      callType,
      enrollmentStepUuid,
    };

    return this.http
      .post<IInitiatePathwayStepSimulationResponse>(
        `${this.backendUrl}/startCall/initiatePathwayStepSimulation`,
        body
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'executed' : 'did not execute';
          console.log(`initiatePathwayStepSimulation: ${outcome}`);
        }),
        catchError((error) => {
          const errorHandler =
            this.errorHandlingService.handleError<IInitiatePathwayStepSimulationResponse>(
              'initiatePathwayStepSimulation request'
            );
          try {
            const result = errorHandler(error);
            if (result instanceof Observable) {
              result.subscribe();
            }
          } catch (e) {
            // expected - rethrow original error
          }
          return throwError(() => error);
        })
      );
  }

  getAssemblyAiToken(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/transcription/getRealTimeTokenAssemblyAI`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getAssemblyAiToken: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getAssemblyAiToken`)
        )
      );
  }

  endCall(
    practiceCallUuid: string,
    chatHistory: string,
    transcriptChanges: ITranscriptCorrection[]
  ): Observable<any> {
    this.logger.log(
      '[CALL] service - guided end request aggregateAndUpdatePracticeCall',
      {
        practiceCallUuid: practiceCallUuid,
        chatHistory: chatHistory,
        transcriptChanges: transcriptChanges,
      }
    );

    return this.http
      .post<any>(`${this.backendUrl}/endCall/aggregateAndUpdatePracticeCall`, {
        practiceCallUuid: practiceCallUuid,
        fullTranscript: chatHistory,
        transcriptChanges: transcriptChanges,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`endCall: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `/endCall/aggregateAndUpdatePracticeCall/`
          )
        )
      );
  }

  endLearnCall(practiceCallUuid: string): Observable<unknown> {
    if (!practiceCallUuid?.trim())
      throw new Error('practiceCallUuid is required');

    const url = `${this.backendUrl}/endCall/endLearnCall?practiceCallUuid=${encodeURIComponent(practiceCallUuid)}`;

    return this.http.post(url, {}, { responseType: 'text' as const, observe: 'response' }).pipe(
      tap((response) => {
        this.logger.log('[CALL] endLearnCall raw response', {
          practiceCallUuid,
          status: response.status,
          body: response.body,
        });
      }),
      map((response) => response.body),
      catchError((error) => {
        this.errorHandlingService.handleError<unknown>('endLearnCall request')(error);
        return throwError(() => error);
      })
    ) as Observable<unknown>;
  }

  sendUserAnswer(
    practiceCallUuid: string,
    questionUuid: string,
    userResponse: string
  ): Observable<any> {
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');
    if (!questionUuid.trim()) throw new Error('questionUuid is empty');
    if (!userResponse.trim()) throw new Error('userResponse is empty');

    return this.http
      .post<any>(
        `${this.backendUrl}/results/createGuidedResult`,
        {
          practiceCallUuid,
          questionUuid,
          userResponse,
        },
        { observe: 'response' }
      )
      .pipe(
        map((result) => {
          const outcome = result.status === 200 ? 'sent' : 'did not send';
          console.log(`sendUserAnswer: ${outcome}`);
          return result;
        }),
        catchError(this.errorHandlingService.handleError<any>(`sendUserAnswer`))
      );
  }

  startCallConversational(
    moduleAssignmentUuid: string,
    callUuid: string
  ): Observable<any> {
    this.logger.log('[CALL] service - conversational start');

    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    return this.http
      .post<any>(`${this.backendUrl}/startCall/conversational`, {
        moduleAssignmentUuid,
        callUuid,
        userUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`startCallConversational: ${outcome}`);
        }),
        catchError((error) => {
          this.logger.error(
            '[CALL] service - conversational start - error',
            error
          );
          return this.errorHandlingService.handleError<any>(
            `startCallConversational request`
          )(error);
        })
      );
  }

  sendUserAnswerConversational(
    callUuid: string,
    practiceCallUuid: string,
    userResponse: string,
    topClips?: number
  ): Observable<any> {
    if (!callUuid.trim()) throw new Error('callUuid is empty');
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');
    if (!userResponse.trim()) throw new Error('userResponse is empty');

    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    let chatHistory: string[] = [];
    if (localStorage.getItem('convoHistory')) {
      const convoHistory = JSON.parse(
        localStorage.getItem('convoHistory') || ''
      ) as {
        chatHistory: string[];
      };
      chatHistory = convoHistory.chatHistory;
    }

    const params = {
      userUuid,
      callUuid,
      practiceCallUuid,
      chatHistory,
      userResponse,
      ...(topClips && { topClips: Number(topClips) }),
    };

    return this.http
      .post<any>(`${this.backendUrl}/conversational/converse`, params)
      .pipe(
        map((result) => result),
        tap((result) => {
          const outcome = result ? 'fetched' : 'did not find';
          console.log(`sendUserAnswerConversational: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `sendUserAnswerConversational`
          )
        )
      );
  }

  private checkUserUuid(): string | null {
    const userUuid = localStorage.getItem('user_uuid');
    if (!userUuid) {
      window.location.replace('/logout');
      return null;
    }
    return userUuid;
  }

  submitFeedback(
    practiceCallUuid: string,
    userFeedback: boolean
  ): Observable<any> {
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');
    if (userFeedback === undefined) throw new Error('userFeedback is empty');
    return this.http
      .post<any>(
        `${this.backendUrl}/endCall/saveUserFeedback`,
        {
          practiceCallUuid: practiceCallUuid,
          userFeedback: userFeedback,
        },
        { responseType: 'text' as 'json' }
      )
      .pipe(
        tap((message: string) => console.log(`Server Response: ${message}`)),
        catchError(
          this.errorHandlingService.handleError<any>(`Submit User Feedback`)
        )
      );
  }

  startConversation(
    moduleAssignmentUuid: string,
    callUuid: string,
    practiceType: string,
    callType: string,
    personalizedLaunchPayload?: IPersonalizedLaunchPayload | null,
    acknowledgementSessionToken?: string | null
  ): Observable<any> {
    if (!moduleAssignmentUuid.trim())
      throw new Error('moduleAssignmentUuid is empty');
    if (!callUuid.trim()) throw new Error('callUuid is empty');
    if (!practiceType.trim()) throw new Error('practiceType is empty');
    if (!callType.trim()) throw new Error('callType is empty');

    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    const payload: any = {
      userUuid: userUuid,
      moduleAssignmentUuid,
      callUuid,
      practiceType,
      callType,
      isEphemeralFlow: personalizedLaunchPayload?.isEphemeralFlow ?? false,
    };

    if (
      personalizedLaunchPayload?.customizationSelections &&
      personalizedLaunchPayload.customizationSelections.length > 0
    ) {
      payload.customizationSelections =
        personalizedLaunchPayload.customizationSelections;
    }

    // Add acknowledgement token for certification calls
    if (acknowledgementSessionToken) {
      payload.acknowledgementSessionToken = acknowledgementSessionToken;
    }

    return this.http
      .post<any>(
        `${this.backendUrl}/startCall/initiateCallAndStartConversation`,
        payload
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`startConversation: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `startConversation request`
          )
        )
      );
  }

  initiateAndStartFramingCall(
    moduleAssignmentUuid: string,
    callUuid: string,
    practiceType: string,
    callType: string,
    enrollmentStepUuid: string
  ): Observable<any> {
    if (!moduleAssignmentUuid.trim())
      throw new Error('moduleAssignmentUuid is empty');
    if (!callUuid.trim()) throw new Error('callUuid is empty');
    if (!practiceType.trim()) throw new Error('practiceType is empty');
    if (!callType.trim()) throw new Error('callType is empty');
    if (!enrollmentStepUuid.trim())
      throw new Error('enrollmentStepUuid is empty');

    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    const payload: any = {
      userUuid: userUuid,
      moduleAssignmentUuid,
      callUuid,
      practiceType,
      callType,
      enrollmentStepUuid,
    };

    return this.http
      .post<any>(
        `${this.backendUrl}/startCall/initiateAndStartFramingCall`,
        payload
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`initiateAndStartFramingCall: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `initiateAndStartFramingCall request`
          )
        )
      );
  }

  endConversation(
    conversationId: string,
    practiceCallUuid: string,
    isPathwayFlow: boolean = false
  ): Observable<any> {
    // Check if the required parameters are empty
    if (!conversationId.trim()) throw new Error('conversationId is empty');
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');

    // Send the request to the backend
    return this.http
      .post<any>(
        `${this.backendUrl}/endCall/endCallAndConversation?conversationId=${conversationId}&practiceCallUuid=${practiceCallUuid}&isPathwayFlow=${isPathwayFlow}`,
        {},
        { responseType: 'text' as 'json' }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`endConversation: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `/endCall/endCallAndConversation/`
          )
        )
      );
  }

  terminateConversation(
    conversationId: string,
    practiceCallUuid: string,
    isPathwayFlow: boolean = false
  ): Observable<any> {
    // Check if the required parameters are empty
    if (!conversationId.trim()) throw new Error('conversationId is empty');
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');

    // Build URL with query parameters
    let url = `${this.backendUrl}/endCall/terminateConversation?conversationId=${conversationId}&practiceCallUuid=${practiceCallUuid}`;
    if (isPathwayFlow) {
      url += `&isPathwayFlow=true`;
    }

    // Send the request to the backend
    return this.http
      .post<any>(url, {}, { responseType: 'text' as 'json' })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`terminateConversation: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `/endCall/terminateConversation/`
          )
        )
      );
  }

  resumeConversation(
    conversationId: string,
    practiceCallUuid: string
  ): Observable<any> {
    // Check if the required parameters are empty
    if (!conversationId.trim()) throw new Error('conversationId is empty');
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');

    // Send the request to the backend
    return this.http
      .post<any>(
        `${this.backendUrl}/startCall/resumeConversation?conversationId=${conversationId}&practiceCallUuid=${practiceCallUuid}`,
        {}
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`resumeConversation: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `/endCall/resumeConversation/`
          )
        )
      );
  }

  fetchGuidedEchoInfo(
    moduleAssignmentUuid: string,
    callUuid: string
  ): Observable<any> {
    // Check if the required parameters are empty
    if (!moduleAssignmentUuid.trim())
      throw new Error('moduleAssignmentUuid is empty');
    if (!callUuid.trim()) throw new Error('callUuid is empty');

    // Check if the userUuid is empty
    const userUuid = this.checkUserUuid();
    if (!userUuid) return new Observable<any>();

    // Send the request to the backend
    return this.http
      .get<any>(`${this.backendUrl}/call/fetchGuidedEchoInfo`, {
        params: {
          moduleAssignmentUuid,
          callUuid,
        },
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`fetchGuidedEchoInfo: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`fetchGuidedEchoInfo`)
        )
      );
  }

  /**
   * Sends an agent interaction block to the backend for processing.
   * This includes the agent's full response and all speech-to-text transcriptions
   * for a given practice call session.
   *
   * @param agentInteractionRequest - The interaction block data containing:
   *   - practiceCallUuid: Unique identifier for the practice call
   *   - agentSaysFull: Complete agent response text
   *   - sttTranscriptions: Array of interactions transcript
   * @returns Observable containing the backend response
   * @throws Error if the request object is null or undefined
   */
  sendAgentInteractionBlock(
    agentInteractionRequest: IAgentInteractionBlockRequest
  ): Observable<any> {
    // Validate that the request object is provided
    if (!agentInteractionRequest)
      throw new Error('agentInteractionRequest is empty');

    // Send the interaction block data to the backend
    return this.http
      .post<any>(
        `${this.backendUrl}/conversational/agentInteractionBlock`,
        agentInteractionRequest,
        { observe: 'response' }
      )
      .pipe(
        map((result) => {
          const outcome = result.status === 200 ? 'sent' : 'failed to send';
          console.log(`sendAgentInteractionBlock: ${outcome}`);
          return result;
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `sendAgentInteractionBlock`
          )
        )
      );
  }

  keepAlive(practiceCallUuid: string): Observable<any> {
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');

    return this.http
      .post<any>(
        `${this.backendUrl}/practiceCall/keepAlive?practiceCallUuid=${practiceCallUuid}`,
        {},
        { responseType: 'text' as 'json' }
      )
      .pipe(
        map((result) => {
          console.log('keepAlive request response:', result);
          return result;
        }),
        catchError(
          this.errorHandlingService.handleError<any>('keepAlive request')
        )
      );
  }

  /**
   * Process user response for conversational calls.
   * Sends user transcript to backend and receives agent response text to speak.
   *
   * @param practiceCallUuid Practice call UUID (auth and Learn/context)
   * @param callUuid Call UUID (CONVERSE prompt and CallLearn)
   * @param userSpokenResponse User's spoken/text input for this turn
   * @param callLearnUuid Optional; not validated in handler
   * @param chatHistory Optional; chat history for the current call
   * @returns Observable with originalAgentResponse and pronunciationCorrectedResponse
   */
  processUserResponse(
    practiceCallUuid: string,
    callUuid: string,
    userSpokenResponse: string,
    callLearnUuid?: string,
    chatHistory?: string[]
  ): Observable<IProcessUserResponseResponse> {
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');
    if (!callUuid.trim()) throw new Error('callUuid is empty');
    if (!userSpokenResponse.trim())
      throw new Error('userSpokenResponse is empty');

    const body: {
      practiceCallUuid: string;
      callUuid: string;
      userSpokenResponse: string;
      callLearnUuid?: string;
      chatHistory?: string[];
    } = {
      practiceCallUuid,
      callUuid,
      userSpokenResponse,
    };
    if (callLearnUuid?.trim()) {
      body.callLearnUuid = callLearnUuid.trim();
    }
    if (chatHistory?.length) {
      body.chatHistory = chatHistory;
    }
    return this.http
      .post<IProcessUserResponseResponse>(
        `${this.backendUrl}/conversational/processUserResponse`,
        body
      )
      .pipe(
        map((result) => {
          this.logger.log('[CALL] processUserResponse: response received', {
            hasOriginal: !!result?.originalAgentResponse,
            hasCorrected: !!result?.pronunciationCorrectedResponse,
          });
          return result;
        }),
        catchError(
          this.errorHandlingService.handleError<IProcessUserResponseResponse>(
            'processUserResponse'
          )
        )
      );
  }

  getAudioStream(
    practiceCallUuid: string,
    textToConvert: string
  ): Observable<ArrayBuffer> {
    if (!practiceCallUuid.trim()) throw new Error('practiceCallUuid is empty');
    if (!textToConvert.trim()) throw new Error('textToConvert is empty');

    return this.http
      .post<ArrayBuffer>(
        `${this.backendUrl}/conversational/audioStream`,
        {
          practiceCallUuid,
          textToConvert,
        },
        {
          responseType: 'arraybuffer' as 'json',
        }
      )
      .pipe(
        tap((buffer) => {
          this.logger.log('[CALL] getAudioStream: audio response received', {
            byteLength: buffer?.byteLength ?? 0,
            hasBuffer: !!buffer,
          });
          if ((buffer?.byteLength ?? 0) < 256) {
            this.logger.warn('[CALL] getAudioStream: unusually small audio payload', {
              byteLength: buffer?.byteLength ?? 0,
              practiceCallUuid,
            });
          }
        }),
        catchError(
          this.errorHandlingService.handleError<ArrayBuffer>('getAudioStream')
        )
      );
  }
}
